// src/context/CartDrawerContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const CartDrawerContext = createContext();

/**
 * CartDrawerProvider manages the UI state of the cart drawer/sheet overlay.
 *
 * Separation of concerns (D-09): this context manages ONLY open/close/peek
 * state. Cart data state (items, quantities, totals) lives in CartContext.
 *
 * State:
 *   isOpen    — full drawer/sheet is open (user-initiated)
 *   isPeeking — auto-peek mode after add-to-cart (dismisses after 2500ms)
 *
 * Functions:
 *   openDrawer()  — opens the drawer, clears any active peek
 *   closeDrawer() — closes the drawer and clears any active peek
 *   peekDrawer()  — triggers a 2500ms auto-peek (skipped if drawer already open)
 */
export const CartDrawerProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPeeking, setIsPeeking] = useState(false);
  const peekTimerRef = useRef(null);

  // Clear peek timer on unmount to avoid state updates on unmounted component
  useEffect(() => {
    return () => {
      clearTimeout(peekTimerRef.current);
    };
  }, []);

  const openDrawer = useCallback(() => {
    clearTimeout(peekTimerRef.current);
    setIsOpen(true);
    setIsPeeking(false);
  }, []);

  const closeDrawer = useCallback(() => {
    clearTimeout(peekTimerRef.current);
    setIsOpen(false);
    setIsPeeking(false);
  }, []);

  /**
   * peekDrawer — triggers a brief auto-peek (Amazon-style "item added" confirmation).
   * Per RESEARCH.md Pitfall 5: if the drawer is already fully open, do not override it.
   */
  const peekDrawer = useCallback(() => {
    if (isOpen) return;
    clearTimeout(peekTimerRef.current);
    setIsPeeking(true);
    peekTimerRef.current = setTimeout(() => {
      setIsPeeking(false);
    }, 2500);
  }, [isOpen]);

  return (
    <CartDrawerContext.Provider
      value={{ isOpen, isPeeking, openDrawer, closeDrawer, peekDrawer }}
    >
      {children}
    </CartDrawerContext.Provider>
  );
};

export const useCartDrawer = () => useContext(CartDrawerContext);
